"use client";

import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Navigate } from "react-router-dom";

const roles = [
  "Growth manager",
  "Creative Strategist",
  "Designer",
  "Creator",
  "Marketing Manager",
  "Media Buyer",
  "CEO",
  "CMO",
  "Other",
];

const companySizes = ["1-5", "6-50", "51-500", "500+"];
const videosTested = ["0-20", "21-50", "51-500", "500+"];
const monthlySpend = ["0 - $ 99K", "100K - $ 1M", "1M - $ 5M", "$ 5M+"];

interface SurveyData {
  fullName: string;
  role: string;
  companyName: string;
  website: string;
  companySize?: string;
  videosTested?: string;
  monthlySpend?: string;
}

export default function QualifySurveySection() {
  const [currentStep, setCurrentStep] = useState(0);
  const [surveyData, setSurveyData] = useState<SurveyData>({
    fullName: "",
    role: "",
    companyName: "",
    website: "",
  });
  const [surveyCompleted, setSurveyCompleted] = useState(false);
  const [websiteError, setWebsiteError] = useState<string | null>(null);

  const totalSteps = 7;

  const isValidUrl = (url: string): boolean => {
    try {
      // Check if it starts with http:// or https://
      if (!url.match(/^https?:\/\//i)) {
        return false;
      }
      // Check if it's a valid URL
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  // Validation for enabling Continue button
  const isStepValid = useCallback(() => {
    if (currentStep === 0) {
      return surveyData.fullName.trim();
    }
    if (currentStep === 1) {
      return surveyData.role;
    }
    if (currentStep === 2) {
      return surveyData.companyName.trim();
    }
    if (currentStep === 3) {
      return surveyData.website.trim() && isValidUrl(surveyData.website);
    }
    if (currentStep === 4) {
      return surveyData.companySize;
    }
    if (currentStep === 5) {
      return surveyData.videosTested;
    }
    if (currentStep === 6) {
      return surveyData.monthlySpend;
    }
    return false;
  }, [currentStep, surveyData]);

  const handleSurveyCompletion = useCallback(() => {
    // Save survey data to backend or localStorage
    localStorage.setItem("surveyCompleted", "true");

    // You may want to save the survey data to your backend here

    // Mark survey as completed
    setSurveyCompleted(true);
  }, []);

  const handleContinue = useCallback(() => {
    if (currentStep < totalSteps - 1) {
      setCurrentStep((prev) => prev + 1);
    } else {
      // Survey completed
      handleSurveyCompletion();
    }
  }, [currentStep, totalSteps, handleSurveyCompletion]);

  // Check if user has already completed the survey
  useEffect(() => {
    const hasCompletedSurvey =
      localStorage.getItem("surveyCompleted") === "true";
    if (hasCompletedSurvey) {
      // If survey is already completed, redirect to dashboard
      setSurveyCompleted(true);
    }
  }, []);

  // Add keyboard event handler
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only proceed if Enter key is pressed and current step is valid
      if (e.key === "Enter" && isStepValid()) {
        // Prevent default form submission behavior
        e.preventDefault();

        // Don't trigger if user is typing in a select dropdown
        const activeElement = document.activeElement;
        const isSelectOpen =
          activeElement?.tagName === "SELECT" &&
          (activeElement as HTMLSelectElement).size > 0;

        if (!isSelectOpen) {
          handleContinue();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isStepValid, handleContinue]);

  // If survey is completed, redirect to dashboard
  if (surveyCompleted) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleInputChange = (field: keyof SurveyData, value: string) => {
    setSurveyData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear website error when user starts typing
    if (field === "website") {
      setWebsiteError(null);
    }
  };

  const handleOptionSelect = (field: keyof SurveyData, value: string) => {
    setSurveyData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const renderQuestion = () => {
    switch (currentStep) {
      case 0:
        return (
          <div className="space-y-6 w-full max-w-sm">
            <label className="block text-lg font-bold text-gray-900 mb-3 text-left">
              Your full name
            </label>
            <input
              type="text"
              id="fullName"
              value={surveyData.fullName}
              onChange={(e) => handleInputChange("fullName", e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && isStepValid()) {
                  e.preventDefault();
                  handleContinue();
                }
              }}
              className="w-full px-6 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 text-left text-base text-gray-600 placeholder-gray-400 bg-white transition-all duration-300 ease-in-out outline-none shadow-sm"
              placeholder="Full name"
              style={{ boxShadow: "none" }}
            />
          </div>
        );
      case 1:
        return (
          <div className="space-y-6 w-full max-w-sm">
            <label className="block text-lg font-bold text-gray-900 mb-3 text-left">
              Your role
            </label>
            <div className="relative">
              <select
                value={surveyData.role}
                onChange={(e) => handleInputChange("role", e.target.value)}
                className="w-full px-6 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 text-left text-base text-gray-600 bg-white transition-all duration-300 ease-in-out outline-none shadow-sm appearance-none pr-10"
              >
                <option value="" className="text-gray-400">
                  Select your role
                </option>
                {roles.map((role) => (
                  <option key={role} value={role} className="text-gray-900">
                    {role}
                  </option>
                ))}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-600">
                <svg
                  className="w-4 h-4 fill-current"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                >
                  <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" />
                </svg>
              </div>
            </div>
          </div>
        );
      case 2:
        return (
          <div className="space-y-6 w-full max-w-sm">
            <label className="block text-lg font-bold text-gray-900 mb-3 text-left">
              Company Name
            </label>
            <input
              type="text"
              id="companyName"
              value={surveyData.companyName}
              onChange={(e) => handleInputChange("companyName", e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && isStepValid()) {
                  e.preventDefault();
                  handleContinue();
                }
              }}
              className="w-full px-6 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 text-left text-base text-gray-600 placeholder-gray-400 bg-white transition-all duration-300 ease-in-out outline-none shadow-sm"
              placeholder="Your company name"
              style={{ boxShadow: "none" }}
            />
          </div>
        );
      case 3:
        return (
          <div className="space-y-6 w-full max-w-sm">
            <label className="block text-lg font-bold text-gray-900 mb-3 text-left">
              Website
            </label>
            <input
              type="url"
              id="website"
              value={surveyData.website}
              onChange={(e) => handleInputChange("website", e.target.value)}
              onBlur={() => {
                if (
                  surveyData.website.trim() &&
                  !isValidUrl(surveyData.website)
                ) {
                  setWebsiteError(
                    "Please enter a valid URL (e.g., https://company.com)"
                  );
                } else {
                  setWebsiteError(null);
                }
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && isStepValid()) {
                  e.preventDefault();
                  handleContinue();
                }
              }}
              className={`w-full px-6 py-2 rounded-lg border ${
                websiteError ? "border-red-500" : "border-gray-200"
              } hover:bg-gray-50 focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 text-left text-base text-gray-600 placeholder-gray-400 bg-white transition-all duration-300 ease-in-out outline-none shadow-sm`}
              placeholder="https://yourcompany.com"
              style={{ boxShadow: "none" }}
            />
            {websiteError && (
              <p className="mt-2 text-sm text-red-600">{websiteError}</p>
            )}
          </div>
        );
      case 4:
        return (
          <div className="space-y-8 w-full max-w-sm">
            <div className="text-lg font-medium text-gray-900 mb-4 text-center">
              Company size
            </div>
            <div className="flex flex-col gap-2 items-center w-full">
              {companySizes.map((size) => (
                <button
                  key={size}
                  type="button"
                  onClick={() => handleOptionSelect("companySize", size)}
                  className={`w-full text-left flex items-center gap-4 px-6 py-2 rounded-lg border transition-colors font-medium text-lg
                    ${
                      surveyData.companySize === size
                        ? "bg-black border-black text-white shadow-sm"
                        : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50 hover:scale-[1.01] hover:shadow-sm cursor-pointer"
                    }
                  `}
                >
                  {size}
                </button>
              ))}
            </div>
          </div>
        );
      case 5:
        return (
          <div className="space-y-8 w-full max-w-sm">
            <div className="text-lg font-medium text-gray-900 mb-4 text-center">
              Videos tested last month
            </div>
            <div className="flex flex-col gap-2 items-center w-full">
              {videosTested.map((videos) => (
                <button
                  key={videos}
                  type="button"
                  onClick={() => handleOptionSelect("videosTested", videos)}
                  className={`w-full text-left flex items-center gap-4 px-6 py-2 rounded-lg border transition-colors font-medium text-lg
                    ${
                      surveyData.videosTested === videos
                        ? "bg-black border-black text-white shadow-sm"
                        : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50 hover:scale-[1.01] hover:shadow-sm cursor-pointer"
                    }
                  `}
                >
                  {videos}
                </button>
              ))}
            </div>
          </div>
        );
      case 6:
        return (
          <div className="space-y-8 w-full max-w-sm">
            <div className="text-lg font-medium text-gray-900 mb-4 text-center">
              Monthly spend in ads
            </div>
            <div className="flex flex-col gap-2 items-center w-full">
              {monthlySpend.map((spend) => (
                <button
                  key={spend}
                  type="button"
                  onClick={() => handleOptionSelect("monthlySpend", spend)}
                  className={`w-full text-left flex items-center gap-4 px-6 py-2 rounded-lg border transition-colors font-medium text-lg
                    ${
                      surveyData.monthlySpend === spend
                        ? "bg-black border-black text-white shadow-sm"
                        : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50 hover:scale-[1.01] hover:shadow-sm cursor-pointer"
                    }
                  `}
                >
                  {spend}
                </button>
              ))}
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen w-full flex flex-col items-center justify-center bg-zinc-50 relative overflow-y-hidden">
      {/* Thin progress bar absolutely positioned under Navbar, matching Navbar width */}
      <div className="absolute left-0 right-0 top-[30px] z-40 flex justify-center pointer-events-none">
        <div className="w-full max-w-[1300px] px-4">
          <div className="h-1 bg-gray-200 rounded-full w-full">
            <motion.div
              className="h-1 bg-blue-600 rounded-full"
              initial={{ width: "0%" }}
              animate={{ width: `${((currentStep + 1) / totalSteps) * 100}%` }}
              transition={{ duration: 0.2 }}
            />
          </div>
        </div>
      </div>
      <div
        className="flex flex-col items-center justify-center flex-1 w-full"
        style={{ minHeight: "100vh" }}
      >
        {/* About you section */}
        <div className="w-full max-w-sm mb-8 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">About you</h2>
          <p className="text-gray-600">
            Please fill out the form to help us offer the best product for you.
          </p>
        </div>

        {/* Question container */}
        <div
          className={`w-full max-w-sm ${
            currentStep < 4 ? "min-h-[140px]" : "min-h-[180px]"
          } ${
            currentStep < 4 ? "mb-0" : "mb-2"
          } flex flex-col items-start justify-center`}
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
              className="w-full"
            >
              {renderQuestion()}
            </motion.div>
          </AnimatePresence>
        </div>
        {/* Continue button close to question */}
        {currentStep < totalSteps - 1 ? (
          <motion.button
            whileHover={isStepValid() ? { scale: 1.01 } : {}}
            whileTap={isStepValid() ? { scale: 0.98 } : {}}
            onClick={isStepValid() ? handleContinue : undefined}
            disabled={!isStepValid()}
            className={`w-full max-w-sm text-[15px] font-medium rounded-lg transition-all shadow-sm hover:cursor-pointer
              ${
                isStepValid()
                  ? "bg-black text-white hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black py-3 px-4"
                  : "bg-gray-100 text-gray-400 py-3 px-4 border border-gray-200 cursor-not-allowed"
              }
            `}
          >
            Continue
          </motion.button>
        ) : (
          <motion.button
            whileHover={isStepValid() ? { scale: 1.01 } : {}}
            whileTap={isStepValid() ? { scale: 0.98 } : {}}
            onClick={isStepValid() ? handleContinue : undefined}
            disabled={!isStepValid()}
            className={`mt-0 w-full max-w-sm text-[15px] font-medium rounded-lg transition-all shadow-sm hover:cursor-pointer
              ${
                isStepValid()
                  ? "bg-black text-white hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black py-3 px-4"
                  : "bg-gray-100 text-gray-400 py-3 px-4 border border-gray-200 cursor-not-allowed"
              }
            `}
          >
            Finish
          </motion.button>
        )}
      </div>
    </div>
  );
}
